import axios from "axios";
import {
  db as clientFirestore,
  functions as firebaseFunctions,
  firebase,
} from "../firebase-config";

// Initializing Asia-South1 functions for notifications
const notificationFunctions = firebase.app().functions("asia-south1");

// Interfaces
interface NotificationOptions {
  userId?: string;
  name?: string;
}

interface NotificationResponse {
  success: boolean;
  message: string;
  subscriptionId?: string;
}

// API Request Interface
interface NotificationRequest {
  email: string;
  sessionId?: string;
  grade: string;
  subject?: string;
  enableNotifications: boolean;
  userId?: string;
  name?: string;
}

/**
 * Helper to sanitize email for use in document IDs
 */
const sanitizeEmail = (email: string): string => {
  return email.replace(/\./g, "_").toLowerCase();
};

/**
 * Generate Firestore document path based on subscription type
 */
const getSubscriptionDocumentPath = (
  email: string,
  type: "session" | "subject" | "grade",
  id?: string,
  subject?: string,
  grade?: string
): string => {
  const sanitizedEmail = sanitizeEmail(email);

  if (type === "session" && id) {
    return `notifications/live_class_subscriptions/subscriptions/${sanitizedEmail}_${id}`;
  } else if (type === "subject" && subject) {
    const gradeComponent = grade ? `_${grade}` : "";
    return `notifications/live_class_subscriptions/subscriptions/${sanitizedEmail}${gradeComponent}_${subject}`;
  } else if (type === "grade" && grade) {
    return `notifications/live_class_subscriptions/subscriptions/${sanitizedEmail}_${grade}`;
  }

  throw new Error("Invalid subscription parameters");
};

/**
 * Subscribe or unsubscribe a user from live class notifications
 *
 * @param email User's email address
 * @param enableNotifications Whether to enable or disable notifications
 * @param userId User's unique ID
 * @param options Additional options (sessionId, grade, subject, etc.)
 * @returns Promise with the result of the operation
 */
export async function manageLiveClassNotifications(
  email: string,
  userId: string,
  enableNotifications: boolean,
  options: {
    sessionId?: string;
    grade: string;
    subject?: string;
    name?: string;
  }
): Promise<NotificationResponse> {
  try {
    if (!email) {
      return {
        success: false,
        message: "Email is required",
      };
    }

    if (!userId) {
      return {
        success: false,
        message: "User ID is required",
      };
    }

    if (!options.grade) {
      return {
        success: false,
        message: "Grade is required",
      };
    }

    // Create the request payload
    const requestData: NotificationRequest = {
      email,
      userId,
      enableNotifications,
      ...options,
    };

    // Call the API endpoint
    const response = await axios.post(
      "/api/live-class-notifications",
      requestData
    );

    return {
      success: response.data?.success || true,
      message:
        response.data?.message ||
        "Notification preferences updated successfully",
      subscriptionId: response.data?.subscriptionId,
    };
  } catch (error: any) {
    console.error("Error managing live class notifications:", error);

    // Format error message based on the response
    let errorMessage =
      "Failed to update notification preferences. Please try again.";

    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
}

/**
 * Subscribe a user to notifications for a specific live class
 *
 * @param email User's email
 * @param sessionId ID of the live class session
 * @param grade Grade level (e.g., "class_9")
 * @param options Additional options including required userId
 * @returns Promise with the result of the operation
 */
export async function subscribeToLiveClass(
  email: string,
  sessionId: string,
  grade: string,
  options: {
    userId: string;
    name?: string;
  }
): Promise<NotificationResponse> {
  const { userId, ...restOptions } = options;
  return manageLiveClassNotifications(email, userId, true, {
    sessionId,
    grade,
    ...restOptions,
  });
}

/**
 * Unsubscribe a user from notifications for a specific live class
 *
 * @param email User's email
 * @param sessionId ID of the live class session
 * @param grade Grade level (e.g., "class_9")
 * @param options Additional options including required userId
 * @returns Promise with the result of the operation
 */
export async function unsubscribeFromLiveClass(
  email: string,
  sessionId: string,
  grade: string,
  options: {
    userId: string;
    name?: string;
  }
): Promise<NotificationResponse> {
  const { userId, ...restOptions } = options;
  return manageLiveClassNotifications(email, userId, false, {
    sessionId,
    grade,
    ...restOptions,
  });
}

/**
 * Subscribe a user to notifications for all live classes in a specific subject
 *
 * @param email User's email
 * @param subject Subject name
 * @param grade Grade level (e.g., "class_9")
 * @param options Additional options including required userId
 * @returns Promise with the result of the operation
 */
export async function subscribeToSubject(
  email: string,
  subject: string,
  grade: string,
  options: {
    userId: string;
    name?: string;
  }
): Promise<NotificationResponse> {
  const { userId, ...restOptions } = options;
  return manageLiveClassNotifications(email, userId, true, {
    subject,
    grade,
    ...restOptions,
  });
}

/**
 * Subscribe a user to notifications for all live classes in a specific grade
 *
 * @param email User's email
 * @param grade Grade level (e.g., "class_9")
 * @param options Additional options including required userId
 * @returns Promise with the result of the operation
 */
export async function subscribeToGrade(
  email: string,
  grade: string,
  options: {
    userId: string;
    name?: string;
  }
): Promise<NotificationResponse> {
  const { userId, ...restOptions } = options;
  return manageLiveClassNotifications(email, userId, true, {
    grade,
    ...restOptions,
  });
}

/**
 * Unsubscribe a user from all live class notifications
 *
 * @param email User's email
 * @param userId User's unique ID
 * @param grade Grade level (e.g., "class_9")
 * @returns Promise with the result of the operation
 */
export async function unsubscribeFromAllNotifications(
  email: string,
  userId: string,
  grade: string
): Promise<NotificationResponse> {
  return manageLiveClassNotifications(email, userId, false, { grade });
}

/**
 * Response from checkSubscriptionStatus cloud function
 */
interface SubscriptionStatusResponse {
  success: boolean;
  isSubscribed: boolean;
  subscriptionType?: "session" | "subject" | "grade" | "gradeAndSubject" | null;
  subscriptions?: {
    bySession: Record<string, any>;
    byGrade: Record<string, any>;
    bySubject: Record<string, any>;
    byGradeAndSubject: Record<string, any>;
  };
}

/**
 * Check the subscription status for a user with rich details
 * @param email - User's email address
 * @param options - Options to check (sessionId, subject, grade)
 * @returns Promise with detailed subscription status
 */
export async function checkSubscriptionStatus(
  email: string,
  options: {
    sessionId?: string;
    subject?: string;
    grade: string;
  }
): Promise<SubscriptionStatusResponse> {
  try {
    // Get current user
    const user = firebase.auth().currentUser;
    if (!user) {
      console.warn("Cannot check subscription: No authenticated user");
      return {
        success: false,
        isSubscribed: false,
        subscriptionType: null,
        subscriptions: {
          bySession: {},
          byGrade: {},
          bySubject: {},
          byGradeAndSubject: {},
        },
      };
    }

    const { sessionId, subject, grade } = options;

    if (!grade) {
      console.warn("Cannot check subscription: Grade is required");
      return {
        success: false,
        isSubscribed: false,
        subscriptionType: null,
        subscriptions: {
          bySession: {},
          byGrade: {},
          bySubject: {},
          byGradeAndSubject: {},
        },
      };
    }

    // Log what we're checking
    console.log(`Checking subscription status for ${email}`, options);

    // Try to use the Firebase Cloud Function directly
    try {
      const checkSubscriptionStatusFn = notificationFunctions.httpsCallable(
        "checkSubscriptionStatus"
      );

      // Call the function with all relevant parameters
      const result = await checkSubscriptionStatusFn({
        email,
        sessionId,
        subject,
        grade,
      });

      // Return the rich response
      return result.data;
    } catch (fnError) {
      console.error("Error calling subscription cloud function:", fnError);

      // Fall back to the API endpoint
      try {
        // Construct query parameters
        const params = new URLSearchParams();
        params.append("email", email);
        params.append("userId", user.uid);
        params.append("grade", grade); 

        if (sessionId) params.append("sessionId", sessionId);
        if (subject) params.append("subject", subject);

        const response = await fetch(
          `/api/check-subscription-status?${params.toString()}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.ok) {
          return await response.json();
        }
      } catch (apiError) {
        console.error("Error checking subscription from API:", apiError);
      }

      // Return a default failed response
      return {
        success: false,
        isSubscribed: false,
        subscriptionType: null,
        subscriptions: {
          bySession: {},
          byGrade: {},
          bySubject: {},
          byGradeAndSubject: {},
        },
      };
    }
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return {
      success: false,
      isSubscribed: false,
      subscriptionType: null,
      subscriptions: {
        bySession: {},
        byGrade: {},
        bySubject: {},
        byGradeAndSubject: {},
      },
    };
  }
}

/**
 * Check if a user is subscribed to notifications for a specific class
 * @param email - User's email address
 * @param classId - ID of the class to check
 * @param grade - Grade level (e.g., "class_9")
 * @returns Promise<boolean> - True if subscribed, false otherwise
 * @deprecated Use checkSubscriptionStatus instead for more detailed information
 */
export async function checkClassSubscription(
  email: string,
  classId: string,
  grade: string
): Promise<boolean> {
  const result = await checkSubscriptionStatus(email, {
    sessionId: classId,
    grade,
  });
  return result.isSubscribed;
}

/**
 * Check if a user is subscribed to a specific subject
 * @param {string} email - User's email address
 * @param {string} subject - Subject to check
 * @param {string} grade - Grade to check for (e.g., "class_7")
 * @returns {Promise<boolean>} - True if subscribed, false otherwise
 * @deprecated Use checkSubscriptionStatus instead for more detailed information
 */
export async function checkSubjectSubscription(
  email: string,
  subject: string,
  grade: string
): Promise<boolean> {
  const result = await checkSubscriptionStatus(email, { subject, grade });
  return result.isSubscribed;
}

/**
 * Check if a user is subscribed to a specific grade
 * @param {string} email - User's email address
 * @param {string} grade - Grade to check (e.g., "class_7")
 * @returns {Promise<boolean>} - True if subscribed, false otherwise
 * @deprecated Use checkSubscriptionStatus instead for more detailed information
 */
export async function checkGradeSubscription(
  email: string,
  grade: string
): Promise<boolean> {
  const result = await checkSubscriptionStatus(email, { grade });
  return result.isSubscribed;
}

/**
 * Unsubscribe a user from all notifications for a specific subject
 * @param {string} email - User's email address
 * @param {string} subject - Subject to unsubscribe from
 * @param {string} grade - Grade level (e.g., "class_9")
 * @param {NotificationOptions} options - Additional options
 * @returns {Promise<NotificationResponse>}
 */
export async function unsubscribeFromSubject(
  email: string,
  subject: string,
  grade: string,
  options: NotificationOptions = {}
): Promise<NotificationResponse> {
  try {
    // Log the unsubscribe request
    console.log(
      `Unsubscribing ${email} from all ${subject} classes in grade ${grade}`
    );

    // Call the API endpoint via fetch
    const response = await fetch("/api/live-class-notifications", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        subject,
        grade,
        enableNotifications: false,
        userId: options.userId,
        name: options.name,
      }),
    });

    const result = await response.json();

    return {
      success: result.success !== false,
      message:
        result.message ||
        `You've been unsubscribed from all ${subject} classes in grade ${grade.replace(
          "class_",
          ""
        )}`,
    };
  } catch (error) {
    console.error("Error unsubscribing from subject:", error);
    return {
      success: false,
      message: "Failed to unsubscribe from subject",
    };
  }
}

/**
 * Unsubscribe a user from all notifications for a specific grade
 * @param {string} email - User's email address
 * @param {string} grade - Grade to unsubscribe from (e.g., "class_9")
 * @param {NotificationOptions} options - Additional options
 * @returns {Promise<NotificationResponse>}
 */
export async function unsubscribeFromGrade(
  email: string,
  grade: string,
  options: NotificationOptions = {}
): Promise<NotificationResponse> {
  try {
    // Extract grade number for display
    const gradeNumber = grade.replace("class_", "");

    // Log the unsubscribe request
    console.log(`Unsubscribing ${email} from all grade ${gradeNumber} classes`);

    // Call the API endpoint
    const response = await fetch("/api/live-class-notifications", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        grade,
        enableNotifications: false,
        userId: options.userId,
        name: options.name,
      }),
    });

    const result = await response.json();

    return {
      success: result.success !== false,
      message:
        result.message ||
        `You've been unsubscribed from all grade ${gradeNumber} classes`,
    };
  } catch (error) {
    console.error("Error unsubscribing from grade:", error);
    return {
      success: false,
      message: "Failed to unsubscribe from grade",
    };
  }
}
